import React, { useEffect, useRef, useState } from 'react';
import { useWallet } from '../../context/WalletContext';
import { useContracts } from '../../context/ContractContext';
import Card from '../common/Card';
import InlineLoader from '../common/InlineLoader';
import { formatUSDT, timeAgo, actTypeLabel, actTypeColor } from '../../utils/format';

const PAGE = 15;

export default function ActivityFeed() {
  const { account } = useWallet();
  const { main, views } = useContracts();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pg, setPg] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!account) { setLoading(false); return; }
    const load = async () => {
      try {
        const uid = await main.addrToId(account);
        if (uid.eq(0)) { if (mountedRef.current) setLoading(false); return; }
        const res = await views.getUserActs(uid, pg, PAGE);
        if (!mountedRef.current) return;
        const rows = [];
        const totalNum = Number(res.total.toString());
        const len = res.timestamps.length;
        for (let i = len - 1; i >= 0; i--) {
          rows.push({
            i,
            ts: res.timestamps[i],
            amt: res.amounts[i],
            type: Number(res.actTypes[i]),
            fromLevel: res.fromLevels[i],
            fromUid: res.fromUserIds[i]
          });
        }
        setItems(rows);
        setTotal(totalNum);
        setLoading(false);
      } catch (_) {
        if (mountedRef.current) setLoading(false);
      }
    };
    setLoading(true);
    load();
    return () => { mountedRef.current = false; };
  }, [account, main, views, pg]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE));

  if (loading) return <Card title="Recent Activity"><InlineLoader /></Card>;
  if (!account) return <Card title="Recent Activity"><div className="empty-state">Connect wallet</div></Card>;
  if (items.length === 0) return <Card title="Recent Activity"><div className="empty-state">No activity yet</div></Card>;

  return (
    <Card title={`Recent Activity (${total})`}>
      <div>
        {items.map((it, idx) => {
          const label = actTypeLabel(it.type);
          const color = actTypeColor(it.type);
          const desc =
            it.type === 1 ? `Investment of $${formatUSDT(it.amt)}` :
            it.type === 2 ? `ROI accrued` :
            it.type === 3 ? `Level ${it.fromLevel.toString()} income from ID #${it.fromUid.toString()}` :
            it.type === 4 ? `Withdrawal approved` :
            it.type === 5 ? `Booster income` :
            it.type === 6 ? `Withdrawal requested` :
            it.type === 7 ? `Withdrawal rejected` : 'Activity';
          const amtColor =
            it.type === 4 ? 'text-warning' :
            it.type === 1 ? 'text-primary' :
            it.type === 6 ? 'text-warning' :
            it.type === 7 ? 'text-danger' :
            'text-success';
          const sign =
            it.type === 4 || it.type === 6 ? '−' :
            it.type === 7 ? '+' :
            '+';
          return (
            <div key={idx} className="activity-item">
              <div className="act-left">
                <span className={`act-icon ${color}`}>{label.slice(0, 1)}</span>
                <div>
                  <div className="act-desc">{desc}</div>
                  <div className="act-time">{timeAgo(it.ts)}</div>
                </div>
              </div>
              <div className={`act-amount ${amtColor}`}>
                {sign}${formatUSDT(it.amt)}
              </div>
            </div>
          );
        })}
      </div>
      {totalPages > 1 && (
        <div className="pagination">
          <button className="page-btn" disabled={pg === 0} onClick={() => setPg((p) => Math.max(0, p - 1))}>← PREV</button>
          <span className="page-info">Page {pg + 1} / {totalPages}</span>
          <button className="page-btn" disabled={pg >= totalPages - 1} onClick={() => setPg((p) => p + 1)}>NEXT →</button>
        </div>
      )}
    </Card>
  );
}
